import React, { createContext, useContext, useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import api from '../services/api';

interface SiteConfig {
  site_name: string;
  site_description: string;
  maintenance_mode: boolean;
  debug_mode: boolean;
  logo_url: string;
  logo_width: number;
  logo_height: number;
}

interface SiteConfigContextType {
  siteConfig: SiteConfig | null;
  loading: boolean;
  error: string | null;
  refreshConfig: () => Promise<void>;
}

const SiteConfigContext = createContext<SiteConfigContextType | undefined>(undefined);

export const useSiteConfig = () => {
  const context = useContext(SiteConfigContext);
  if (context === undefined) {
    throw new Error('useSiteConfig must be used within a SiteConfigProvider');
  }
  return context;
};

interface SiteConfigProviderProps {
  children: React.ReactNode;
}

export const SiteConfigProvider: React.FC<SiteConfigProviderProps> = ({ children }) => {
  const [siteConfig, setSiteConfig] = useState<SiteConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasPermissionError, setHasPermissionError] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const location = useLocation();

  const fetchSiteConfig = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('🔄 开始获取网站配置...');
      
      // 检查当前是否在公开页面（主页、价格表、分享页面）
      const publicPaths = ['/', '/pricing'];
      const isPublicPath = publicPaths.includes(window.location.pathname) || 
                          window.location.pathname.startsWith('/share/');
      const isAuthPage = window.location.pathname === '/login' || window.location.pathname === '/register';
      
      if (isPublicPath || isAuthPage) {
        // 公开页面（主页、价格表、分享页面、登录/注册）直接使用公开API
        console.log('🔓 公开页面，使用公开API获取配置');
        try {
          console.log('📡 调用公开配置API...');
          const response = await api.get('/admin/system/public-config');
          const config = response.data;
          console.log('✅ 公开获取到系统配置:', config);
          
          const siteConfigData: SiteConfig = {
            site_name: config.site_name || '',
            site_description: config.site_description || '',
            maintenance_mode: false,
            debug_mode: false,
            logo_url: config.logo_url || '',
            logo_width: config.logo_width || 32,
            logo_height: config.logo_height || 32
          };
          
          setSiteConfig(siteConfigData);
          setError(null);
          setHasPermissionError(false);
          updatePageMeta(siteConfigData);
          return;
        } catch (publicError) {
          console.warn('⚠️ 公开配置API失败:', publicError);
          setError('无法获取网站配置');
          return;
        }
      }
      
      // 其他页面需要检查用户权限
      const userResponse = await api.get('/auth/me');
      const user = userResponse.data;
      console.log('👤 当前用户:', user);
      
      if (user.role !== 'admin') {
        // 普通用户获取公开的系统配置
        console.log('👤 普通用户，使用公开API获取系统配置');
        try {
          console.log('📡 调用公开配置API...');
          const response = await api.get('/admin/system/public-config');
          const config = response.data;
          console.log('✅ 普通用户获取到系统配置:', config);
          console.log('📊 配置详情:', {
            site_name: config.site_name,
            site_description: config.site_description,
            logo_url: config.logo_url
          });
          
          const siteConfigData: SiteConfig = {
            site_name: config.site_name || '',
            site_description: config.site_description || '',
            maintenance_mode: false,
            debug_mode: false,
            logo_url: config.logo_url || '',
            logo_width: config.logo_width || 32,
            logo_height: config.logo_height || 32
          };
          
          setSiteConfig(siteConfigData);
          setError(null);
          setHasPermissionError(false); // 普通用户也可以正常刷新
          updatePageMeta(siteConfigData);
          return;
        } catch (configError) {
          console.error('❌ 无法获取系统配置:', configError);
          setError('无法获取网站配置');
          return;
        }
      }
      
      // 管理员用户访问系统配置
      console.log('📡 调用管理员配置API...');
      const response = await api.get('/admin/system/config');
      const config = response.data;
      console.log('✅ 管理员获取到系统配置:', config);
      console.log('📊 配置详情:', {
        site_name: config.site_name,
        site_description: config.site_description,
        logo_url: config.logo_url
      });
      
      // 提取网站相关的配置
      const siteConfigData: SiteConfig = {
        site_name: config.site_name || '',
        site_description: config.site_description || '',
        maintenance_mode: config.maintenance_mode || false,
        debug_mode: config.debug_mode || false,
        logo_url: config.logo_url || '',
        logo_width: config.logo_width || 32,
        logo_height: config.logo_height || 32
      };
      
      setSiteConfig(siteConfigData);
      setHasPermissionError(false); // 管理员用户，重置权限错误标记
      
      // 更新页面标题和meta描述
      updatePageMeta(siteConfigData);
      
    } catch (error: any) {
      console.error('获取网站配置失败:', error);
      
      // 如果是权限错误，不设置error
      if (error.response?.status === 403) {
        setError(null); // 权限错误不显示为错误
        setHasPermissionError(true); // 标记权限错误，停止定期刷新
      } else {
        setError(error.response?.data?.message || '获取网站配置失败');
      }
    } finally {
      setLoading(false);
    }
  };

  const updatePageMeta = (config: SiteConfig) => {
    // 只有在有有效配置时才更新页面标题
    if (config && config.site_name) {
      document.title = config.site_name;
    }
    
    // 更新meta描述
    if (config && config.site_description) {
      const metaDescription = document.querySelector('meta[name="description"]');
      if (metaDescription) {
        metaDescription.setAttribute('content', config.site_description);
      } else {
        // 如果不存在meta描述标签，创建一个
        const meta = document.createElement('meta');
        meta.name = 'description';
        meta.content = config.site_description;
        document.head.appendChild(meta);
      }
    }
  };

  const refreshConfig = async () => {
    await fetchSiteConfig();
  };

  useEffect(() => {
    // 只在首次加载时获取配置，避免每次页面切换都重新加载
    if (!isInitialized) {
      fetchSiteConfig().finally(() => {
        setIsInitialized(true);
      });
    }
  }, [isInitialized]);

  useEffect(() => {
    // 只有在没有权限错误时才设置定期刷新配置（每5分钟检查一次）
    if (!hasPermissionError) {
      const interval = setInterval(() => {
        fetchSiteConfig();
      }, 5 * 60 * 1000);
      
      return () => clearInterval(interval);
    }
  }, [hasPermissionError]);

  const value: SiteConfigContextType = {
    siteConfig,
    loading,
    error,
    refreshConfig
  };

  return (
    <SiteConfigContext.Provider value={value}>
      {children}
    </SiteConfigContext.Provider>
  );
};





